import {
  buildParentMap,
  createShapeElement,
  imageFromPlaceholder,
  isImagePlaceholder,
  type SceneDocument,
} from "@cva/shared";
import type { EditorStore } from "./store";

/**
 * The cell a dragged image of a collage is dropped onto: an image or image
 * placeholder of the same group under the pointer (the dragged one itself
 * is skipped). Undefined when the drag is an ordinary move.
 */
export function collageSwapTarget(
  doc: SceneDocument,
  draggedId: string,
  clientX: number,
  clientY: number,
  page: HTMLElement | null,
): string | undefined {
  const dragged = doc.elements[draggedId];
  if (dragged?.type !== "image") return undefined;
  const parents = buildParentMap(doc);
  const parent = parents.get(draggedId);
  if (parent?.kind !== "group") return undefined;
  for (const node of document.elementsFromPoint(clientX, clientY)) {
    if (!page?.contains(node)) continue;
    const id = (node as HTMLElement).closest<HTMLElement>("[data-element-id]")?.dataset.elementId;
    if (!id || id === draggedId) continue;
    const element = doc.elements[id];
    const sameGroup = (() => {
      const other = parents.get(id);
      return other?.kind === "group" && other.groupId === parent.groupId;
    })();
    if (!element || !sameGroup || element.locked) return undefined;
    if (element.type === "image" || isImagePlaceholder(element)) return id;
    return undefined;
  }
  return undefined;
}

/**
 * Swaps the pictures of two collage cells (the boxes stay where they are).
 * Onto an empty cell the picture moves and leaves an empty cell behind.
 */
export function swapCellImages(store: EditorStore, draggedId: string, targetId: string) {
  store.getState().update((draft) => {
    const dragged = draft.elements[draggedId];
    const target = draft.elements[targetId];
    if (dragged?.type !== "image" || !target) return;
    if (target.type === "image") {
      const assetId = target.assetId;
      target.assetId = dragged.assetId;
      dragged.assetId = assetId;
      target.crop = null;
      dragged.crop = null;
      return;
    }
    if (!isImagePlaceholder(target)) return;
    draft.elements[targetId] = imageFromPlaceholder(target, dragged.assetId);
    draft.elements[draggedId] = createShapeElement("rect", {
      id: dragged.id,
      name: dragged.name ?? "Bild",
      x: dragged.x,
      y: dragged.y,
      width: dragged.width,
      height: dragged.height,
      rotation: dragged.rotation,
      opacity: dragged.opacity,
      radius: dragged.radius,
      fill: target.fill,
      placeholder: "image",
      ...(dragged.mask ? { mask: { ...dragged.mask } } : {}),
    });
  });
}
